export type TextGenerateSplitBy = "words" | "characters";

export interface EstimateTextGenerateCompleteMsOptions {
	duration?: number;
	staggerDelay?: number;
	splitBy?: TextGenerateSplitBy;
	/**
	 * When set, wall-clock from first token start → last token finish is fixed
	 * (stagger is derived from token count). Seconds.
	 */
	totalDuration?: number;
}

/** Token count matching {@link TextGenerateEffect} stagger units. */
export function countTextGenerateTokens(
	words: string,
	splitBy: TextGenerateSplitBy = "words",
): number {
	const segments = words
		.split(/\n/)
		.map((s) => s.trim())
		.filter(Boolean);
	if (segments.length === 0) {
		return 0;
	}
	if (splitBy === "characters") {
		return segments.reduce(
			(sum, segment) => sum + Array.from(segment).length,
			0,
		);
	}
	return segments.reduce(
		(sum, segment) => sum + segment.split(/\s+/).filter(Boolean).length,
		0,
	);
}

/**
 * Resolve per-token fade duration + stagger so the full sequence fits
 * `totalDuration` when provided; otherwise use the explicit stagger.
 */
export function resolveTextGenerateTiming(options: {
	tokenCount: number;
	duration: number;
	staggerDelay: number;
	totalDuration?: number;
}): { duration: number; staggerDelay: number; completeMs: number } {
	const n = Math.max(0, options.tokenCount);
	if (n === 0) {
		return { duration: 0, staggerDelay: 0, completeMs: 0 };
	}

	if (options.totalDuration != null && options.totalDuration > 0) {
		const total = options.totalDuration;
		const duration = Math.min(options.duration, total);
		const staggerDelay =
			n <= 1 ? 0 : Math.max(0, (total - duration) / (n - 1));
		return {
			duration,
			staggerDelay,
			completeMs: Math.ceil(total * 1000),
		};
	}

	return {
		duration: options.duration,
		staggerDelay: options.staggerDelay,
		completeMs: Math.ceil(
			((n - 1) * options.staggerDelay + options.duration) * 1000,
		),
	};
}

/**
 * Approximates when the last token finishes animating for {@link TextGenerateEffect},
 * matching its stagger formula (or fixed {@link EstimateTextGenerateCompleteMsOptions.totalDuration}).
 */
export function estimateTextGenerateCompleteMs(
	words: string,
	options?: EstimateTextGenerateCompleteMsOptions,
): number {
	const duration = options?.duration ?? 0.4;
	const staggerDelay = options?.staggerDelay ?? 0.03;
	const n = countTextGenerateTokens(words, options?.splitBy ?? "words");
	return resolveTextGenerateTiming({
		tokenCount: n,
		duration,
		staggerDelay,
		totalDuration: options?.totalDuration,
	}).completeMs;
}

/**
 * Offset from a segment's animation start until the **next** chained segment should begin,
 * so stagger feels continuous (`tokenCount * staggerDelay`), without waiting for each token's
 * full opacity/blur duration. Pair with {@link estimateTextGenerateCompleteMs} when you need
 * decorate/callback timing after the segment truly finishes.
 */
export function estimateTextGenerateChainMs(
	words: string,
	options?: EstimateTextGenerateCompleteMsOptions,
): number {
	const duration = options?.duration ?? 0.4;
	const staggerDelay = options?.staggerDelay ?? 0.03;
	const n = countTextGenerateTokens(words, options?.splitBy ?? "words");
	if (n === 0) {
		return 0;
	}
	const timing = resolveTextGenerateTiming({
		tokenCount: n,
		duration,
		staggerDelay,
		totalDuration: options?.totalDuration,
	});
	return Math.ceil(n * timing.staggerDelay * 1000);
}
